<div class="main-view space-y-2 md:space-y-4">
    <div class="flex flex-wrap items-start gap-2">
        <div class="min-w-0 flex-1 space-y-1">
            <app-page-surface-header titleIcon="clients"
                [compact]="true"
                [title]="client?.tenant_name || 'Client details'"
                subtitle="Client profile and agents assigned to this client."
            />
            <p *ngIf="client?.unique_id" class="text-xs text-gray-500 dark:text-gray-400">
                Reference ID: <span class="font-mono">{{ client!.unique_id }}</span>
            </p>
        </div>
        <a
            *ngIf="client && tenantUuid"
            [routerLink]="['/manage-client', tenantUuid, 'edit']"
            class="btn inline-flex shrink-0 items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white"
        >
            <icon-edit class="size-4" />
            Edit client
        </a>
    </div>

    <div *ngIf="loadError" class="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
        {{ loadError }}
        <div class="mt-4">
            <a routerLink="/manage-client" class="btn inline-flex rounded-full bg-white px-4 py-2 text-black">Back to clients</a>
        </div>
    </div>

    <div *ngIf="loading && !loadError" class="rounded-3xl border border-gray-200 bg-white/60 p-8 dark:border-[#191e3a] dark:bg-[#0e1726]">
        <p class="text-sm text-gray-500">Loading client…</p>
    </div>

    <ng-container *ngIf="client && !loadError">
        <!-- Client profile -->
        <div class="rounded-3xl border border-gray-200 bg-white/60 p-6 shadow-sm dark:border-[#191e3a] dark:bg-[#0e1726] md:p-8">
            <div class="flex flex-wrap items-start gap-4">
                <div *ngIf="client.logo" class="flex h-16 w-44 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white px-2 dark:border-[#191e3a] dark:bg-[#0e1726]">
                    <img [src]="client.logo" class="max-h-14 max-w-full object-contain" [alt]="client.tenant_name || 'Client logo'" />
                </div>
                <div
                    *ngIf="!client.logo && client.tenant_name"
                    class="grid h-16 w-16 shrink-0 place-content-center rounded-2xl bg-primary text-xl font-semibold text-white"
                >
                    {{ initials(client.tenant_name) }}
                </div>
                <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2">
                        <h2 class="text-xl font-bold text-gray-900 dark:text-white">{{ client.tenant_name || '—' }}</h2>
                        <span
                            class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold"
                            [ngClass]="clientStatusBadgeClass(client.status)"
                        >
                            {{ client.status || '—' }}
                        </span>
                    </div>
                    <p *ngIf="client.tenant_subdomain" class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                        {{ client.tenant_subdomain }}
                    </p>
                </div>
            </div>

            <div class="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Contact person</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">{{ client.contact_person || '—' }}</p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Phone</p>
                    <p class="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {{
                            client.contact_number
                                | tenantPhone : client.contact_number_country_code : client.contact_number_country_iso
                        }}
                    </p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Email</p>
                    <p class="mt-1 truncate text-sm font-medium text-gray-900 dark:text-white">{{ client.email || '—' }}</p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">GSTIN</p>
                    <p class="mt-1 font-mono text-sm text-gray-900 dark:text-white">{{ client.gstn || '—' }}</p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Business PAN</p>
                    <p class="mt-1 font-mono text-sm text-gray-900 dark:text-white">{{ client.business_pan || '—' }}</p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Billing address</p>
                    <p class="mt-1 text-sm text-gray-900 dark:text-white">{{ billingAddressLine(client) }}</p>
                </div>
                <div class="rounded-xl bg-gray-50 px-4 py-3 dark:bg-white/[0.04]">
                    <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Products / services</p>
                    <p class="mt-1 text-sm text-gray-900 dark:text-white">
                        <ng-container *ngIf="client.selected_product_slugs?.length || client.selected_service_slugs?.length; else noCatalog">
                            <span *ngIf="client.selected_product_slugs?.length">{{ client.selected_product_slugs!.join(', ') }}</span>
                            <span *ngIf="client.selected_product_slugs?.length && client.selected_service_slugs?.length"> · </span>
                            <span *ngIf="client.selected_service_slugs?.length">{{ client.selected_service_slugs!.join(', ') }}</span>
                        </ng-container>
                        <ng-template #noCatalog>—</ng-template>
                    </p>
                </div>
            </div>
        </div>

        <!-- Billing configuration -->
        <div class="rounded-3xl border border-gray-200 bg-white/60 p-6 shadow-sm dark:border-[#191e3a] dark:bg-[#0e1726] md:p-8">
            <h5 class="mb-4 text-lg font-semibold">Billing Type</h5>
            <p class="text-xl font-bold">{{ isPostpaid ? 'Postpaid' : 'Prepaid' }}</p>

            <ng-container *ngIf="isPostpaid">
                <div class="mt-5 grid gap-4 sm:grid-cols-2">
                    <div *ngFor="let charge of productCharges">
                        <p class="text-sm text-gray-500 dark:text-gray-400">{{ charge.name }} — charge per PD</p>
                        <p class="font-semibold">₹ {{ charge.amount }}</p>
                    </div>
                    <div>
                        <p class="text-sm text-gray-500 dark:text-gray-400">Billing Frequency</p>
                        <p class="font-semibold">{{ billingFrequencyLabel }}</p>
                    </div>
                    <div>
                        <p class="text-sm text-gray-500 dark:text-gray-400">Outstanding (un-invoiced)</p>
                        <p class="font-semibold">₹ {{ outstandingBalance }}</p>
                    </div>
                </div>
                <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    This tenant is billed on usage — one charge per completed PD — not on a subscription.
                </p>
            </ng-container>

            <p *ngIf="!isPostpaid" class="mt-4 text-sm text-gray-500 dark:text-gray-400">
                This tenant is billed through subscription plans. Plan assignment and usage quotas are not yet available.
            </p>
        </div>

        <!-- Associated agents -->
        <app-page-body-panel>
            <ng-container ngProjectAs="[pageToolbar]">
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h3 class="text-base font-bold text-gray-900 dark:text-white">Associated agents</h3>
                        <p class="text-xs text-gray-500 dark:text-gray-400">
                            {{ agentsTotal }} agent{{ agentsTotal === 1 ? '' : 's' }} linked to this client
                        </p>
                    </div>
                    <a
                        routerLink="/create-agent/agent-details"
                        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white"
                    >
                        <icon-plus-circle class="size-4" />
                        Create agent
                    </a>
                </div>
            </ng-container>

            <div class="table-responsive mt-1">
                <table class="table table-hover table-surface">
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Agent name</th>
                            <th>Product</th>
                            <th>Status</th>
                            <th>Deployed</th>
                            <th class="text-end">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngIf="agentsLoading">
                            <td colspan="6" class="py-10 text-center text-sm text-gray-500">Loading agents…</td>
                        </tr>
                        <tr *ngIf="!agentsLoading && !agents.length">
                            <td colspan="6" class="py-10 text-center text-sm text-gray-500">
                                No agents assigned to this client yet.
                            </td>
                        </tr>
                        <tr *ngFor="let agent of agents" class="border-none">
                            <td class="font-mono text-xs">{{ agent.agent_code || '—' }}</td>
                            <td class="font-medium text-primary dark:text-white">{{ agent.name }}</td>
                            <td>{{ productName(agent) }}</td>
                            <td>
                                <span
                                    class="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold"
                                    [ngClass]="agentStatusBadgeClass(agent.status)"
                                >
                                    {{ agent.status }}
                                </span>
                            </td>
                            <td class="text-sm text-gray-600 dark:text-gray-300">
                                {{ agent.deployed_at ? (agent.deployed_at | appDate) : '—' }}
                            </td>
                            <td class="text-end">
                                <a
                                    [routerLink]="['/create-agent/agent-details']"
                                    [queryParams]="{ agent: agent.uuid }"
                                    class="text-xs font-semibold text-primary hover:underline"
                                >
                                    Open
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </app-page-body-panel>
        <app-audit-history *ngIf="client" resourceType="tenant" [resourceId]="client.id" class="mt-5 block"></app-audit-history>
    </ng-container>
</div>
